// Activity log / audit trail (Sprint 10, docs/03 §Audit). A read-only, keyset-
// paginated feed of who did what — surfaced in Settings → Activity for admins and
// owners. Every mutating action across the API, assistant, MCP and public API is
// recorded with its origin (`via`). Types live here (not @seencast/shared yet),
// mirroring the C2 contract exactly.

import { useInfiniteQuery } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type { ListEnvelope } from './client.ts';

/** Where an audited action originated. */
export type AuditVia = 'ui' | 'assistant' | 'mcp' | 'api';
export const AUDIT_VIA: readonly AuditVia[] = ['ui', 'assistant', 'mcp', 'api'];

export interface AuditEntry {
  id: string;
  created_at: number;
  /** null for system/API-key actors — label from `via` in that case. */
  user: { id: string; name: string } | null;
  via: AuditVia;
  /** Verb, e.g. "schedule.create", "screen.reload". Rendered as-is (humanized in UI). */
  action: string;
  entity_type: string;
  entity_id: string;
  /** Present when the entity still exists / had a name; null for deleted or unnamed. */
  entity_name: string | null;
  /** Free-form structured payload; shown pretty-printed in the expandable row. */
  details_json: Record<string, unknown> | null;
}

/** Query filters — all optional; empty values are dropped by the client. */
export interface AuditFilters {
  entity_type?: string;
  user_id?: string;
  via?: AuditVia;
  /** ISO date (YYYY-MM-DD) inclusive lower/upper bound. */
  from?: string;
  to?: string;
}

const PAGE_SIZE = 25;

export function listAudit(
  filters: AuditFilters,
  cursor?: string,
): Promise<ListEnvelope<AuditEntry>> {
  return request<ListEnvelope<AuditEntry>>('/audit', {
    query: { limit: PAGE_SIZE, cursor, ...filters },
  });
}

/**
 * Keyset ("Load more") pagination over the audit feed. Each page carries the
 * opaque `next_cursor`; null means the end. Filters are part of the query key so
 * changing a filter starts a fresh keyset.
 */
export function useAudit(filters: AuditFilters) {
  return useInfiniteQuery({
    queryKey: qk.audit(filters),
    queryFn: ({ pageParam }) => listAudit(filters, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}
